import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Alert, Button, Modal, Table, Tag, Typography } from 'antd'
import { ExclamationCircleOutlined } from '@ant-design/icons'
import { getMyVibeDatasetIngestionErrors, getMyVibeDatasetIngestions } from '../../../request'
import { DATASET_IMPORT_MODE_META } from '../../Profile/vibeDataset'
import { formatDateTime } from '../terminalModel'

const { Text } = Typography

// 状态/来源文案迁移自 src/views/Profile/VibeDatasetPanel.js（Profile 原文件不改）。
const INGESTION_STATUS_META = {
  registering: { text: '登记中', color: 'processing' },
  registered: { text: '已登记', color: 'green' },
  prepared: { text: '待上传', color: 'default' },
  uploading: { text: '上传中', color: 'blue' },
  ready: { text: '待提交', color: 'cyan' },
  queued: { text: '已排队', color: 'blue' },
  validating: { text: '校验中', color: 'processing' },
  publishing: { text: '发布中', color: 'processing' },
  success: { text: '成功', color: 'green' },
  failed: { text: '失败', color: 'red' },
  cancelled: { text: '已取消', color: 'default' },
  expired: { text: '已过期', color: 'default' }
}

const SOURCE_TYPE_META = {
  excel: 'Excel',
  csv: 'CSV',
  dingtalk_ai_table: '钉钉 AI 表格',
  image: '图片',
  url: 'URL',
  pdf: 'PDF',
  api: 'API',
  manual: '手工整理'
}

// 进行中的批次需要 5s 轮询；登记中/待上传/上传中/待提交/已排队/校验中/发布中
const ACTIVE_INGESTION_STATUSES = new Set([
  'registering', 'prepared', 'uploading', 'ready', 'queued', 'validating', 'publishing'
])

const POLL_INTERVAL = 5000

const EMPTY_PAGE = { pageNum: 1, pageSize: 10, total: 0, list: [] }

function renderIngestionStatus (status) {
  const meta = INGESTION_STATUS_META[status] || { text: status || '-', color: 'default' }
  return <Tag color={meta.color}>{meta.text}</Tag>
}

/**
 * 操作 tab「导入记录」分区（owner 或数据 Owner，即 detail.canManage）。
 * 逻辑复制改造自 src/views/Profile/VibeDatasetPanel.js 的批次表/错误弹窗/轮询，Profile 原文件不改。
 * 有活跃批次时 5s 轮询当前页；组件卸载或切项目时清理定时器。
 * 错误明细按 afterId 分页加载；L3 红线区不展示值预览。
 */
export default function IngestionHistorySection ({ projectId, canManage, sensitivityLevel }) {
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState(false)
  const [page, setPage] = useState(EMPTY_PAGE)
  const [errorVisible, setErrorVisible] = useState(false)
  const [errorBatch, setErrorBatch] = useState(null)
  const [errors, setErrors] = useState([])
  const [errorLoading, setErrorLoading] = useState(false)
  const [errorLoadFailed, setErrorLoadFailed] = useState(false)
  const [errorHasMore, setErrorHasMore] = useState(false)
  const [errorNextAfterId, setErrorNextAfterId] = useState(0)
  const mountedRef = useRef(true)
  const pollTimerRef = useRef(null)

  const hasProjectId = projectId !== null && projectId !== undefined && projectId !== ''
  const showValuePreview = String(sensitivityLevel || '').toUpperCase() !== 'L3'

  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false }
  }, [])

  const clearPollTimer = () => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current)
      pollTimerRef.current = null
    }
  }

  const loadIngestions = useCallback((pageNum = 1, showLoading = true) => {
    if (!hasProjectId) return Promise.resolve()
    clearPollTimer()
    if (showLoading) {
      setLoading(true)
      setLoadError(false)
    }
    return getMyVibeDatasetIngestions({ projectId, pageNum, pageSize: 10 })
      .then(result => {
        if (!mountedRef.current) return
        setPage({
          pageNum: (result && result.pageNum) || pageNum,
          pageSize: (result && result.pageSize) || 10,
          total: (result && result.total) || 0,
          list: Array.isArray(result && result.list) ? result.list : []
        })
        setLoadError(false)
      })
      .catch(() => {
        if (mountedRef.current) setLoadError(true)
      })
      .finally(() => {
        if (mountedRef.current && showLoading) setLoading(false)
      })
  }, [hasProjectId, projectId])

  useEffect(() => {
    if (!canManage || !hasProjectId) return
    loadIngestions(1)
  }, [canManage, hasProjectId, loadIngestions])

  // 活跃批次轮询：依赖当前页数据，页面变化后重新判定；卸载/翻页时由 cleanup 清理定时器。
  useEffect(() => {
    clearPollTimer()
    if (!canManage || !hasProjectId) return undefined
    const hasActiveBatch = page.list.some(item => ACTIVE_INGESTION_STATUSES.has(item.status))
    if (!hasActiveBatch) return undefined
    pollTimerRef.current = setTimeout(() => {
      pollTimerRef.current = null
      loadIngestions(page.pageNum, false)
    }, POLL_INTERVAL)
    return clearPollTimer
  }, [canManage, hasProjectId, loadIngestions, page])

  const loadErrors = (batch, afterId, reset) => {
    if (!batch || errorLoading) return
    setErrorLoading(true)
    setErrorLoadFailed(false)
    getMyVibeDatasetIngestionErrors({
      projectId,
      ingestionId: batch.ingestionId,
      afterId,
      limit: 100
    })
      .then(result => {
        if (!mountedRef.current) return
        const incoming = Array.isArray(result && result.list) ? result.list : []
        setErrors(previous => (reset ? incoming : previous.concat(incoming)))
        setErrorHasMore(Boolean(result && result.hasMore))
        setErrorNextAfterId((result && result.nextAfterId) || afterId)
      })
      .catch(() => {
        if (mountedRef.current) setErrorLoadFailed(true)
      })
      .finally(() => {
        if (mountedRef.current) setErrorLoading(false)
      })
  }

  const openErrors = batch => {
    setErrorVisible(true)
    setErrorBatch(batch || null)
    setErrors([])
    setErrorHasMore(false)
    setErrorNextAfterId(0)
    setErrorLoadFailed(false)
    loadErrors(batch, 0, true)
  }

  const closeErrors = () => setErrorVisible(false)

  const batchColumns = [
    {
      title: '来源',
      key: 'source',
      width: 190,
      render: (_, record) => (
        <div>
          <div>{record.sourceName || '-'}</div>
          <Text type="secondary">{SOURCE_TYPE_META[record.sourceType] || record.sourceType || '-'}</Text>
        </div>
      )
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 90,
      render: renderIngestionStatus
    },
    {
      title: '分片',
      key: 'chunks',
      width: 80,
      render: (_, record) => `${record.receivedChunkCount || 0}/${record.expectedChunkCount || 0}`
    },
    {
      title: '数据行',
      key: 'rows',
      width: 140,
      render: (_, record) => (
        <div>
          <div>写入 {record.writtenRowCount || 0}</div>
          <Text type={record.rejectedRowCount ? 'danger' : 'secondary'}>
            拒绝 {record.rejectedRowCount || 0}
          </Text>
        </div>
      )
    },
    {
      title: '模式',
      dataIndex: 'importMode',
      width: 130,
      render: value => DATASET_IMPORT_MODE_META[value] || value || '-'
    },
    {
      title: '更新时间',
      dataIndex: 'updateTime',
      width: 155,
      render: value => formatDateTime(value)
    },
    {
      title: '操作',
      key: 'action',
      width: 90,
      fixed: 'right',
      render: (_, record) => (
        record.status === 'failed' || Number(record.rejectedRowCount || 0) > 0
          ? <Button
            type="link"
            size="small"
            icon={<ExclamationCircleOutlined />}
            onClick={() => openErrors(record)}
          >
            错误
          </Button>
          : null
      )
    }
  ]

  const errorColumns = [
    { title: '行号', dataIndex: 'sourceRowNo', width: 80, render: value => (value === undefined || value === null ? '-' : value) },
    { title: '表', dataIndex: 'logicalTableName', width: 130, render: value => <Text code>{value || '-'}</Text> },
    { title: '字段', dataIndex: 'fieldName', width: 130, render: value => <Text code>{value || '-'}</Text> },
    { title: '错误代码', dataIndex: 'errorCode', width: 150, render: value => <Text code>{value || '-'}</Text> },
    { title: '错误说明', dataIndex: 'errorMessage', width: 260 },
    ...(showValuePreview
      ? [{ title: '值预览', dataIndex: 'valuePreview', width: 140, render: value => value || '-' }]
      : []),
    { title: '时间', dataIndex: 'createTime', width: 155, render: value => formatDateTime(value) }
  ]

  if (!canManage) {
    return <section className="term-ops-section">
      <div className="term-ops-head">
        <span className="term-ops-title">导入记录</span>
      </div>
      <div className="term-ops-card">
        <div className="term-ops-note">仅项目登记人或数据 Owner（上传人）可查看导入记录。</div>
      </div>
    </section>
  }

  const modalFooter = [
    errorHasMore
      ? <Button key="more" loading={errorLoading} onClick={() => loadErrors(errorBatch, errorNextAfterId, false)}>加载更多</Button>
      : null,
    <Button key="close" onClick={closeErrors}>关闭</Button>
  ].filter(Boolean)

  return <section className="term-ops-section">
    <div className="term-ops-head">
      <span className="term-ops-title">导入记录</span>
      <span className="term-ops-desc">展示最近导入批次；有进行中的批次时每 5 秒自动刷新。</span>
      <button
        type="button"
        className="term-link term-ops-head-action"
        disabled={loading}
        onClick={() => loadIngestions(page.pageNum)}
      >
        {loading ? '刷新中…' : '刷新'}
      </button>
    </div>
    <div className="term-ops-card">
      {loadError
        ? <Alert
          type="error"
          showIcon
          message="导入记录加载失败"
          action={<button type="button" className="term-link" onClick={() => loadIngestions(page.pageNum)}>重试</button>}
        />
        : <Table
          className="term-ops-table"
          rowKey={record => String(record.id || record.ingestionId)}
          size="small"
          loading={loading}
          columns={batchColumns}
          dataSource={page.list}
          scroll={{ x: 900 }}
          locale={{ emptyText: '暂无导入记录' }}
          pagination={{
            current: page.pageNum,
            pageSize: page.pageSize,
            total: page.total,
            showSizeChanger: false,
            onChange: pageNum => loadIngestions(pageNum)
          }}
        />}
    </div>

    <Modal
      open={errorVisible}
      width={1050}
      title={`导入错误：${(errorBatch && (errorBatch.sourceName || errorBatch.ingestionId)) || '-'}`}
      footer={modalFooter}
      onCancel={closeErrors}
      destroyOnClose
      wrapClassName="term-ops-modal"
    >
      {errorLoadFailed
        ? <Alert
          type="error"
          showIcon
          message="错误明细加载失败"
          action={<button type="button" className="term-link" onClick={() => loadErrors(errorBatch, errorNextAfterId, !errors.length)}>重试</button>}
          style={{ marginBottom: 12 }}
        />
        : null}
      {errorBatch && errorBatch.errorSummary
        ? <Alert type="error" showIcon message={errorBatch.errorSummary} style={{ marginBottom: 12 }} />
        : null}
      <Table
        className="term-ops-table"
        rowKey={record => String(record.id)}
        size="small"
        loading={errorLoading && !errors.length}
        columns={errorColumns}
        dataSource={errors}
        pagination={false}
        scroll={{ x: 1050, y: 480 }}
        locale={{ emptyText: '没有结构化错误明细' }}
      />
    </Modal>
  </section>
}
